import { useEffect, useRef, useState } from 'react';

const versionPollMs = 60_000;

interface VersionPayload {
  version?: string;
}

export function useAppVersionRefresh() {
  const currentVersionRef = useRef<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let intervalId = 0;

    async function checkVersion() {
      if (cancelled || updateAvailable || document.visibilityState !== 'visible') return;

      try {
        const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) return;

        const payload = await response.json() as VersionPayload;
        const nextVersion = typeof payload.version === 'string' ? payload.version : '';
        if (!nextVersion) return;

        if (!currentVersionRef.current) {
          currentVersionRef.current = nextVersion;
          return;
        }

        if (currentVersionRef.current !== nextVersion) setUpdateAvailable(true);
      } catch {
        // Version checks are best-effort; app should continue normally offline.
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') void checkVersion();
    };

    void checkVersion();
    intervalId = window.setInterval(() => void checkVersion(), versionPollMs);
    window.addEventListener('focus', checkVersion);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', checkVersion);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [updateAvailable]);

  return {
    updateAvailable,
    refresh: () => window.location.reload(),
  };
}
